import React, { useState } from 'react';

import { useFormValidation } from '@techchange/components';

import {
	resourceFormDataReducers as formDataReducers,
	RESOURCE_FORM_DATA_DISPATCH
} from '../reducers/resourceFormData';

export const RESOURCE_FORM_FIELDS = {
	CONTENT: 'content',
	DATA: 'data',
	LANGUAGE: 'language',
	TITLE: 'title',
	WIDTH: 'width',
	BULK: 'bulk',
};

/**
 * Initializes and manages form data for editing a resource.
 *
 * @param {Function} initializeFormData  Function to initialize the form state.
 * @param {Function} initializeInputValidation  Function to initialize the input validation state.
 * @param {Object} props  The props from the consuming component.
 * @param {Object} props.defaultResourceContent  The default value for new resource content.
 * @param {String} props.id  The resource ID.
 * @param {Boolean} props.isNew  Flag indicating whether resource is being created.
 * @param {String} props.type  The resource type.
 * @param {String} props.userLanguage  The current user's language setting.
 * @param {Function} props.onLanguageChange  Callback to handle language change.
 * @param {Function} props.onWidthChange  Callback to handle width change.
 * @param {Function} props.onSave  Callback to handle form submission.
 *
 * @returns {Object} result
 * @returns {Object} result.formData  The form state.
 * @returns {Object} result.inputValidation  The input validation state.
 * @returns {Object} result.actions  The set of actions for updating and submitting the form.
 * @returns {Function} result.actions.addTranslation  Handles adding a new translation.
 * @returns {Function} result.actions.deleteTranslation  Handles removing an existing translation.
 * @returns {Function} result.actions.submitForm  Handles the form submission.
 * @returns {Function} result.actions.updateField  Handles changes to HTML form inputs.
 * @returns {Function} result.actions.updateLanguage  Handles changes to the language dropdown.
 * @returns {Function} result.actions.updateSrc  Handles changes to an image upload input.
 */
export const useResourceFormData = (initializeFormData, initializeInputValidation, props) => {

	const [formData, setFormData] = useState(initializeFormData);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { inputValidation, validateFormData } = useFormValidation(initializeInputValidation());

	/**
	 * Updates the form data given changes to a form field input.
	 *
	 * @param {String} fieldName  The name of the field to update.
	 * @param {String} fieldValue  The new value of the field.
	 */
	const updateField = (fieldName, fieldValue) => {

		const type = chooseActionType(fieldName);

		const reducer = formDataReducers[type];

		const updatedFormData = reducer(formData, {
			fieldName,
			fieldValue,
			defaultLanguage: props.defaultLanguage,
		});

		// Only validate on field changes after first form submission attempt
		if (hasSubmitted) {
			validateFormData(updatedFormData);
		}

		setFormData(updatedFormData);

		if (fieldName === RESOURCE_FORM_FIELDS.WIDTH) {
			props.onWidthChange(props.id, updatedFormData);
		}

	};

	/**
	 * Handles change to language select input.
	 *
	 * If the translation for the selected language has not already been loaded,
	 * will fetch the resource content for the selected language.
	 *
	 * @param {String} fieldName  The name of the field to update.
	 * @param {String} fieldValue  The new value of the field.
	 */
	const updateLanguage = async (fieldName, fieldValue) => {

		const isValid = validateFormData(formData);

		// Prevent user from switching languages until
		// form errors are fixed
		if (!isValid) {
			setHasSubmitted(true);
			return;
		}

		// Reset submission state for new translation form view
		setHasSubmitted(false);

		const reducer = formDataReducers[RESOURCE_FORM_DATA_DISPATCH.UPDATE_LANGUAGE];

		const updatedFormData = await reducer(formData, {
			fieldValue,
			id: props.id,
			isNew: props.isNew,
			onLanguageChange: props.onLanguageChange,
		});

		setFormData(updatedFormData);

	};

	/**
	 * Adds new translation to current resource contents state.
	 */
	const addTranslation = () => {

		const reducer = formDataReducers[RESOURCE_FORM_DATA_DISPATCH.ADD_TRANSLATION];

		const updatedFormData = reducer(formData, {
			defaultContent: props.defaultResourceContent,
		});

		setFormData(updatedFormData);

	};

	/**
	 * Removes translation from current resource contents state
	 * and adds to list of resource contents to delete when form
	 * is submitted.
	 */
	const deleteTranslation = () => {

		const reducer = formDataReducers[RESOURCE_FORM_DATA_DISPATCH.DELETE_TRANSLATION];

		const updatedFormData = reducer(formData);

		setFormData(updatedFormData);

	};

	/**
	 * Validates the form data and saves the final results
	 * to the database.
	 *
	 * @param {Object} event  The Javascript event object.
	 */
	const submitForm = async (event) => {

		event.preventDefault();

		if (isSubmitting) {
			return;
		}

		setHasSubmitted(true);

		const isValid = validateFormData(formData);

		// Prevent user from saving changes until
		// form errors are fixed
		if (!isValid) {
			return;
		}

		setIsSubmitting(true);

		// Add responsive image sources
		const reducer = formDataReducers[RESOURCE_FORM_DATA_DISPATCH.UPDATE_IMAGE_SOURCES];

		const updatedFormData = await reducer(formData, {
			resourceType: props.type,
			defaultLanguage: props.defaultLanguage,
		});

		setFormData(updatedFormData);

		// Persist the updates
		await props.onSave({
			...updatedFormData,
			// Return content in the user's current language
			language: props.userLanguage,
			id: props.id,
			type: props.type,
		});

		setIsSubmitting(false);

	};

	return {
		formData,
		isSubmitting,
		inputValidation,
		actions: {
			addTranslation,
			deleteTranslation,
			submitForm,
			updateField,
			updateLanguage,
		},
	};

};

/**
 * Returns the corresponding form data dispatch action given the
 * form field name that was updated.
 *
 * @param {String} fieldName  The name of the field that was updated.
 *
 * @returns {String}  The corresponding form data dispatch action.
 */
function chooseActionType(fieldName) {

	switch (fieldName) {

	case RESOURCE_FORM_FIELDS.BULK:
		return RESOURCE_FORM_DATA_DISPATCH.UPDATE_BULK;
	case RESOURCE_FORM_FIELDS.CONTENT:
	case RESOURCE_FORM_FIELDS.TITLE:
		return RESOURCE_FORM_DATA_DISPATCH.UPDATE_RESOURCE_CONTENTS;

	case RESOURCE_FORM_FIELDS.WIDTH:
		return RESOURCE_FORM_DATA_DISPATCH.UPDATE_WIDTH;

	default:
		return RESOURCE_FORM_DATA_DISPATCH.UPDATE_DATA;

	}

}

/**
 * Determines the initial language for the resource form state.
 *
 * @param {Object} props  The component props.
 * @param {Boolean} props.isNew  Flag indicating whether resource is being created.
 * @param {String} props.userLanguage  The current user's language setting.
 * @param {String} props.defaultLanguage  The default language for the product.
 * @param {Object} props.initialValues  The initial values for the form.
 * @param {String} props.initialValues.language  The currently selected language for the form.
 * @param {Array} props.initialValues.resourceContents  The initial resource contents for the form.
 *
 * @returns {String}  The initial language value.
 */
export function getInitialLanguage(props) {

	// If this is a brand new resource, start
	// with the default language
	if (props.isNew) {
		return props.defaultLanguage;
	}

	// Otherwise set to the currently selected language,
	// or the user's language setting.
	const requestedLanguage =
		props.initialValues.language || props.userLanguage || props.defaultLanguage;

	const hasTranslation =
		props.initialValues.resourceContents &&
		props.initialValues.resourceContents.find(
			(rContent) => rContent.language === requestedLanguage
		);

	if (hasTranslation) {
		return requestedLanguage;
	}

	// If no translation exists for the requested language,
	// fall back to the default
	return props.defaultLanguage;

}
